import React, { useMemo, useState, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  Chip,
  Divider,
  Fade,
  CircularProgress,
  Alert,
  Stack,
  Button,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import MapIcon from '@mui/icons-material/Map'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined'
import { zIndexLayers } from '../../MuiTheme'
import { getBuildVersion } from '../../utils/versionCheck'
import { trackEvent } from '../../services/analyticsService'
import type {
  GetProductUpdatesResult,
  ProductUpdateRelease,
  ProductRoadmapItem,
  ProductUpdatesVoteState,
  ProductVoteCampaign,
  RoadmapItemStatus,
} from '../../../../types/ProductUpdates'
import { castRoadmapVote, revokeRoadmapVote } from '../../services/productUpdatesService'
import {
  formatVoteDeadline,
  roadmapVoteLeadingItemIds,
} from '../../utils/productUpdatesRoadmapVoteHelpers'
import {
  getLatestReleaseIdFromPayload,
  setLastSeenReleaseId,
} from '../../utils/productUpdatesLastSeen'
import { alpha, useTheme } from '@mui/material/styles'
import { productUpdateHighlightSx } from './productUpdateHighlightSx'
import { ProductUpdateMarkdown } from './ProductUpdateMarkdown'
import {
  SEVV_DIALOG_PAPER_BACKGROUND_PALE_TOP,
  SEVV_DIALOG_PAPER_BORDER,
  SEVV_DIALOG_PAPER_SHADOW,
  SEVV_DIALOG_TITLE_COLOR,
  SEVV_DIALOG_TITLE_SUBTITLE_COLOR,
  SEVV_PRIMARY_CSS,
  SEVV_PRIMARY_GRADIENT_VERTICAL,
  sevvDialogTitleChromeSx,
} from '../../theme/sevvDialogChrome'

const FONT = "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', Arial, sans-serif"

const ROADMAP_ORDER: RoadmapItemStatus[] = ['in_progress', 'planned', 'researching', 'shipped']

const ROADMAP_LABELS: Record<RoadmapItemStatus, string> = {
  in_progress: 'In progress',
  planned: 'Planned',
  researching: 'Researching',
  shipped: 'Shipped',
}

function formatHighlightKindLabel(kind: string): string {
  return kind.charAt(0).toUpperCase() + kind.slice(1)
}

function sortReleases(releases: ProductUpdateRelease[]): ProductUpdateRelease[] {
  return [...releases].sort((a, b) => b.date.localeCompare(a.date))
}

function groupRoadmap(items: ProductRoadmapItem[]): Record<RoadmapItemStatus, ProductRoadmapItem[]> {
  const g: Record<RoadmapItemStatus, ProductRoadmapItem[]> = {
    shipped: [],
    in_progress: [],
    planned: [],
    researching: [],
  }
  for (const it of items) {
    if (g[it.status]) g[it.status].push(it)
  }
  return g
}

function campaignMapFromPayload(voteCampaigns: ProductVoteCampaign[] | undefined) {
  return new Map((voteCampaigns ?? []).map((c) => [c.id, c]))
}

function isVoteCampaignOpen(campaign: ProductVoteCampaign | undefined, nowMs: number): boolean {
  if (!campaign) return false
  const end = Date.parse(campaign.closesAt)
  return Number.isFinite(end) && nowMs < end
}

const emptyVoteState = (): ProductUpdatesVoteState => ({
  countsByItemId: {},
  votedItemIds: [],
})

export interface ProductUpdatesDialogProps {
  open: boolean
  onClose: () => void
  data: GetProductUpdatesResult | null
  loading: boolean
  error: string | null
  onRetry: () => void
  isAuthenticated: boolean
  onVoteStateChange?: (voteState: ProductUpdatesVoteState) => void
}

export function ProductUpdatesDialog({
  open,
  onClose,
  data,
  loading,
  error,
  onRetry,
  isAuthenticated,
  onVoteStateChange,
}: ProductUpdatesDialogProps) {
  const theme = useTheme()
  const [tab, setTab] = useState(0)
  const [voteBusyId, setVoteBusyId] = useState<string | null>(null)
  const [voteError, setVoteError] = useState<string | null>(null)

  const paperBackground =
    theme.palette.mode === 'dark'
      ? theme.palette.background.paper
      : SEVV_DIALOG_PAPER_BACKGROUND_PALE_TOP

  const sortedReleases = useMemo(
    () => (data?.payload?.releases ? sortReleases(data.payload.releases) : []),
    [data]
  )

  const roadmapGroups = useMemo(
    () => (data?.payload?.roadmap ? groupRoadmap(data.payload.roadmap) : groupRoadmap([])),
    [data]
  )

  const voteState = data?.voteState ?? emptyVoteState()
  const campaignById = useMemo(
    () => campaignMapFromPayload(data?.payload?.voteCampaigns),
    [data?.payload?.voteCampaigns]
  )

  const leadingIds = useMemo(() => {
    const roadmap = data?.payload?.roadmap ?? []
    return roadmapVoteLeadingItemIds(roadmap, data?.payload?.voteCampaigns, voteState.countsByItemId)
  }, [data?.payload?.roadmap, data?.payload?.voteCampaigns, voteState.countsByItemId])

  const nowMs = Date.now()

  const handleCastVote = useCallback(
    async (roadmapItemId: string) => {
      setVoteError(null)
      setVoteBusyId(roadmapItemId)
      try {
        const res = await castRoadmapVote(roadmapItemId)
        onVoteStateChange?.({
          countsByItemId: res.countsByItemId,
          votedItemIds: res.votedItemIds,
        })
        trackEvent('APP_product_updates_vote_cast', { roadmapItemId })
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not record vote'
        setVoteError(msg)
      } finally {
        setVoteBusyId(null)
      }
    },
    [onVoteStateChange]
  )

  const handleRevokeVote = useCallback(
    async (roadmapItemId: string) => {
      setVoteError(null)
      setVoteBusyId(roadmapItemId)
      try {
        const res = await revokeRoadmapVote(roadmapItemId)
        onVoteStateChange?.({
          countsByItemId: res.countsByItemId,
          votedItemIds: res.votedItemIds,
        })
        trackEvent('APP_product_updates_vote_revoke', { roadmapItemId })
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : 'Could not remove vote'
        setVoteError(msg)
      } finally {
        setVoteBusyId(null)
      }
    },
    [onVoteStateChange]
  )

  const handleClose = useCallback(() => {
    if (data?.payload?.releases?.length) {
      const latest = getLatestReleaseIdFromPayload(sortReleases(data.payload.releases))
      if (latest) {
        setLastSeenReleaseId(latest)
        window.dispatchEvent(new CustomEvent('productUpdatesDidMarkSeen'))
      }
    }
    onClose()
  }, [data, onClose])

  const handleTab = (_: React.SyntheticEvent, v: number) => {
    setTab(v)
    trackEvent('APP_product_updates_tab', { tab: v === 0 ? 'releases' : 'roadmap' })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      aria-labelledby="product-updates-title"
      PaperProps={{
        sx: {
          zIndex: zIndexLayers.modals,
          borderRadius: '24px',
          overflow: 'hidden',
          maxHeight: 'min(88vh, 900px)',
          background: paperBackground,
          backgroundImage: theme.palette.mode === 'dark' ? 'none' : undefined,
          color: theme.palette.text.primary,
          border: SEVV_DIALOG_PAPER_BORDER,
          boxShadow: SEVV_DIALOG_PAPER_SHADOW,
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          zIndex: zIndexLayers.modals - 1,
        },
      }}
    >
      <DialogTitle
        id="product-updates-title"
        sx={{
          ...sevvDialogTitleChromeSx,
          py: 2.5,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <NewReleasesIcon sx={{ fontSize: 32 }} aria-hidden />
          <Box sx={{ color: SEVV_DIALOG_TITLE_COLOR }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                lineHeight: 1.2,
                color: SEVV_DIALOG_TITLE_COLOR,
              }}
            >
              What&apos;s new
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontFamily: FONT,
                mt: 0.25,
                color: SEVV_DIALOG_TITLE_SUBTITLE_COLOR,
              }}
            >
              Releases and product roadmap
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            color: SEVV_DIALOG_TITLE_COLOR,
            '&:hover': { bgcolor: 'color-mix(in srgb, var(--sevv-dialog-title-color, #ffffff) 12%, transparent)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box
        sx={{
          px: 2,
          pt: 1.5,
          bgcolor: (t) =>
            t.palette.mode === 'dark'
              ? alpha(t.palette.background.paper, 0.92)
              : alpha(t.palette.background.paper, 0.65),
        }}
      >
        <Tabs
          value={tab}
          onChange={handleTab}
          variant="fullWidth"
          sx={{
            minHeight: 44,
            '& .MuiTab-root': {
              fontFamily: FONT,
              fontWeight: 600,
              textTransform: 'none',
              color: 'text.secondary',
            },
            '& .Mui-selected': { color: `${SEVV_PRIMARY_CSS} !important` },
            '& .MuiTabs-indicator': { backgroundColor: SEVV_PRIMARY_CSS, height: 3, borderRadius: 1 },
          }}
        >
          <Tab icon={<NewReleasesIcon fontSize="small" />} iconPosition="start" label="Releases" />
          <Tab icon={<MapIcon fontSize="small" />} iconPosition="start" label="Roadmap" />
        </Tabs>
      </Box>

      <DialogContent
        sx={{
          pt: 2,
          px: 3,
          pb: 2,
          fontFamily: FONT,
          bgcolor: 'background.default',
          color: 'text.primary',
        }}
      >
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: SEVV_PRIMARY_CSS }} aria-label="Loading updates" />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
            <Box sx={{ mt: 1 }}>
              <Typography
                component="button"
                type="button"
                onClick={onRetry}
                sx={{
                  border: 'none',
                  background: 'none',
                  color: SEVV_PRIMARY_CSS,
                  cursor: 'pointer',
                  fontWeight: 600,
                  textDecoration: 'underline',
                  fontFamily: FONT,
                }}
              >
                Try again
              </Typography>
            </Box>
          </Alert>
        )}

        {!loading && !error && data && (
          <Fade in timeout={280}>
            <Box>
              {tab === 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {sortedReleases.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No releases published yet. Check back soon.
                    </Typography>
                  ) : (
                    sortedReleases.map((rel) => (
                      <Box
                        key={rel.id}
                        sx={{
                          borderRadius: '16px',
                          bgcolor: 'background.paper',
                          border: (t) => `1px solid ${t.palette.divider}`,
                          boxShadow: 1,
                          backgroundImage: 'none',
                          p: 2.5,
                          position: 'relative',
                          pl: 3,
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: 12,
                            bottom: 12,
                            width: 4,
                            borderRadius: 2,
                            background: SEVV_PRIMARY_GRADIENT_VERTICAL,
                          },
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: SEVV_PRIMARY_CSS, fontWeight: 700, letterSpacing: '0.04em' }}
                        >
                          {rel.date}
                        </Typography>
                        <Box sx={{ mt: 0.5 }}>
                          <ProductUpdateMarkdown markdown={rel.title} titleMode />
                        </Box>
                        <Stack component="ul" spacing={1} sx={{ m: 0, mt: 1.5, pl: 0, listStyle: 'none' }}>
                          {rel.highlights.map((h, i) => (
                            <Box
                              component="li"
                              key={`${rel.id}-h-${i}`}
                              sx={{ listStyle: 'none', display: 'flex', alignItems: 'flex-start', gap: 1 }}
                            >
                              <Chip
                                size="small"
                                label={formatHighlightKindLabel(h.kind)}
                                sx={{
                                  flexShrink: 0,
                                  mt: 0.25,
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  height: 22,
                                  ...productUpdateHighlightSx(theme, h.kind),
                                }}
                              />
                              <Box sx={{ flex: 1, minWidth: 0, color: 'text.primary' }}>
                                <ProductUpdateMarkdown markdown={h.text} />
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    ))
                  )}
                </Box>
              )}

              {tab === 1 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {voteError && (
                    <Alert severity="warning" sx={{ mb: 0 }} onClose={() => setVoteError(null)}>
                      {voteError}
                    </Alert>
                  )}
                  {data.payload.roadmap.length === 0 ? (
                    <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                      No roadmap items yet.
                    </Typography>
                  ) : (
                    ROADMAP_ORDER.map((status) => {
                      const items = roadmapGroups[status]
                      if (!items.length) return null
                      return (
                        <Box key={status}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: SEVV_PRIMARY_CSS,
                              mb: 1.5,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                            }}
                          >
                            {ROADMAP_LABELS[status]}
                            <Chip label={items.length} size="small" sx={{ height: 22, fontWeight: 600 }} />
                          </Typography>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {items.map((item) => {
                              const campaign = item.voteCampaignId
                                ? campaignById.get(item.voteCampaignId)
                                : undefined
                              const hasVoting = !!(item.voteCampaignId && campaign)
                              const votingOpen = hasVoting && isVoteCampaignOpen(campaign, nowMs)
                              const count = voteState.countsByItemId[item.id] ?? 0
                              const voted = voteState.votedItemIds.includes(item.id)
                              const isLeading = leadingIds.has(item.id) && count > 0
                              const busy = voteBusyId === item.id

                              return (
                                <Box
                                  key={item.id}
                                  sx={{
                                    p: 2,
                                    borderRadius: '12px',
                                    bgcolor: 'background.paper',
                                    border: (t) => `1px solid ${t.palette.divider}`,
                                    boxShadow: 1,
                                    backgroundImage: 'none',
                                  }}
                                >
                                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    {item.title}
                                  </Typography>
                                  {(item.theme || item.quarter) && (
                                    <Box sx={{ mt: 0.5, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                      {item.theme && (
                                        <Chip
                                          label={item.theme}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 24 }}
                                        />
                                      )}
                                      {item.quarter && (
                                        <Chip
                                          label={item.quarter}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: 24 }}
                                        />
                                      )}
                                    </Box>
                                  )}
                                  <Box sx={{ mt: 1, color: 'text.secondary' }}>
                                    <ProductUpdateMarkdown markdown={item.summary} />
                                  </Box>

                                  {hasVoting && campaign && (
                                    <Box
                                      sx={{
                                        mt: 1.5,
                                        pt: 1.5,
                                        borderTop: (t) => `1px solid ${t.palette.divider}`,
                                      }}
                                    >
                                      <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1} sx={{ mb: 1 }}>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                          Community votes · {count}
                                        </Typography>
                                        {campaign.title && (
                                          <Chip
                                            size="small"
                                            label={campaign.title}
                                            variant="outlined"
                                            sx={{ height: 22, borderColor: 'divider' }}
                                          />
                                        )}
                                        {isLeading && (
                                          <Chip
                                            size="small"
                                            label="Top pick"
                                            color="success"
                                            sx={{ height: 22, fontWeight: 600 }}
                                          />
                                        )}
                                        {!votingOpen && (
                                          <Chip
                                            size="small"
                                            label="Voting closed"
                                            sx={{ height: 22, bgcolor: 'action.hover' }}
                                          />
                                        )}
                                      </Stack>
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        {votingOpen
                                          ? `Closes ${formatVoteDeadline(campaign.closesAt)}`
                                          : `Closed ${formatVoteDeadline(campaign.closesAt)}`}
                                      </Typography>
                                      {votingOpen && (
                                        <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                                          {!isAuthenticated && (
                                            <Typography variant="caption" color="text.secondary">
                                              Sign in to vote on this idea.
                                            </Typography>
                                          )}
                                          {isAuthenticated && !voted && (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="primary"
                                              disabled={busy}
                                              startIcon={<ThumbUpOutlinedIcon />}
                                              onClick={() => void handleCastVote(item.id)}
                                              sx={{ textTransform: 'none', fontWeight: 600 }}
                                            >
                                              Vote
                                            </Button>
                                          )}
                                          {isAuthenticated && voted && (
                                            <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1}>
                                              <Chip
                                                size="small"
                                                icon={<ThumbUpIcon sx={{ '&&': { fontSize: 16 } }} />}
                                                label="You voted"
                                                color="primary"
                                                variant="outlined"
                                                sx={{ height: 28, fontWeight: 600 }}
                                              />
                                              <Button
                                                size="small"
                                                variant="text"
                                                color="inherit"
                                                disabled={busy}
                                                onClick={() => void handleRevokeVote(item.id)}
                                                sx={{ textTransform: 'none', fontWeight: 600 }}
                                              >
                                                Remove vote
                                              </Button>
                                            </Stack>
                                          )}
                                        </Stack>
                                      )}
                                    </Box>
                                  )}
                                </Box>
                              )
                            })}
                          </Box>
                        </Box>
                      )
                    })
                  )}
                </Box>
              )}
            </Box>
          </Fade>
        )}

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'space-between',
            gap: 1,
            typography: 'caption',
            color: 'text.secondary',
            fontFamily: FONT,
          }}
        >
          <span>Build {getBuildVersion()}</span>
          {data?.updatedAt && (
            <span>Content updated {new Date(data.updatedAt).toLocaleString()}</span>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  )
}
